import React from 'react';
import { argsToStringifiable } from '@local/finsera-core/src/helpers/embed/utils';
import { _, mui } from '@local/finsera-core/src/libs';
import * as types from '@local/finsera-core/src/types';

import type { ChartRef } from './chart';
import Chart from './chart';
import { useUiStyles } from '../../../hooks';
import CenteredLoader from '../../centered-loader';
import ChartError from '../../chart-error';
import BaseLegend from '../shared/base-legend';
import { useNormalizedSeriesAndColors } from '../shared/use-normalized-series-and-colors';

type NestedBarChartProps = {
  id: string;
  data: types.widgets.TableData;
  count?: types.widgets.CountData;
  fullScreen: boolean;
  readOnly: boolean;

  alert?: {
    type: 'error' | 'warning';
    message: string;
  };

  hideFromChart?: string[];
  tooltipText?: string;
  hideLegend?: boolean;
  numberFormat?: string | null;
  exportTitle?: string;
  exportSubTitle?: string;
  download?: boolean;
  category?: string;
  yAxisLabel?: string;
  xAxisLabel?: string;
  rotation?: number;
  setExternalEmbed?: (_data: types.analysis.EmbedChartData) => void;
  seriesOrder?: (_c: string[]) => string[];
  seriesColorByName?: Record<string, string>;
  displayNumbers?: boolean;
  stacked?: boolean;
  negativeColor?: string;
  positiveColor?: string;
  horizontal?: boolean;
  colorByBar?: boolean;
  minAxisValue?: number;
};

const NestedBarChart = (args: NestedBarChartProps) => {
  const uiStyles = useUiStyles();

  const {
    id,
    data,
    count,
    fullScreen,
    readOnly,
    alert,
    hideFromChart,
    tooltipText = '[bold]{name}:[/] {valueY}',
    hideLegend,
    category = 'history',
    numberFormat = '#.##',
    exportTitle,
    exportSubTitle,
    download = true,
    setExternalEmbed,
    yAxisLabel,
    xAxisLabel,
    rotation = undefined,
    seriesOrder = (c: string[]) => c,
    seriesColorByName,
    displayNumbers = false,
    stacked = false,
    negativeColor = undefined,
    positiveColor = undefined,
    horizontal = false,
    colorByBar = false,
    minAxisValue = undefined,
  } = args;

  const [localData, setLocalData] = React.useState<types.widgets.TableData>(null);
  const chartRef = React.useRef<ChartRef>(null);
  const [hiddenSeries, setHiddenSeries] = React.useState<Set<string>>(new Set());

  const { seriesColorByName: localSeriesColorByName } = useNormalizedSeriesAndColors({
    localData,
    xAxisField: category,
    seriesColorByName,
  });

  const seriesKeys = React.useMemo(() => {
    if (!localData || localData.length === 0) return [];
    return Object.keys(localData[0])
      .filter((key) => key !== category)
      .filter((key) => !hideFromChart?.includes(key));
  }, [localData, category]);

  const shouldShowLegend = !hideLegend && !_.isEmpty(seriesKeys) && (fullScreen || seriesKeys.length <= 6);

  React.useEffect(() => {
    const handleData = async () => {
      setLocalData(null);
      if (_.isNil(data)) return;
      await new Promise((resolve) => setTimeout(resolve, 200));

      if (setExternalEmbed)
        setExternalEmbed({
          id,
          widgetTitle: exportTitle,
          widgetSubTitle: exportSubTitle,
          chart_type: 'nested-bar-chart-v5',
          args: argsToStringifiable({
            ...args,
            data,
          }),
        });

      setLocalData(data);
    };

    handleData();
  }, [data]);

  return (
    <mui.core.Box
      sx={{
        height: '100%',
        position: 'relative',
        overflow: 'hidden',
        display: 'grid',
        gridTemplateRows: `${alert ? 'auto ' : ''} 1fr`,
      }}
    >
      {alert && <ChartError readOnly={readOnly} error={alert.message} severity={alert.type} />}
      <mui.core.Box sx={uiStyles.widgetContent}>
        <mui.core.Box
          sx={{
            height: fullScreen ? 'calc(100% - 40px)' : '100%',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {_.isNil(localData) ? (
            <CenteredLoader label="Loading widget data..." top="-40px" />
          ) : (
            <>
              <mui.core.Box sx={{ flex: 1, minHeight: 0 }}>
                <Chart
                  ref={chartRef}
                  id={id}
                  data={localData}
                  count={count}
                  stacked={stacked}
                  fullScreen={fullScreen}
                  numberFormat={numberFormat}
                  download={download}
                  rotation={rotation}
                  yAxisLabel={yAxisLabel}
                  xAxisLabel={xAxisLabel}
                  category={category}
                  tooltipText={tooltipText}
                  seriesOrder={seriesOrder}
                  seriesColorByName={localSeriesColorByName}
                  displayNumbers={displayNumbers}
                  negativeColor={negativeColor}
                  positiveColor={positiveColor}
                  horizontal={horizontal}
                  colorByBar={colorByBar}
                  minAxisValue={minAxisValue}
                  hideFromChart={hideFromChart}
                />
              </mui.core.Box>
              {shouldShowLegend && !_.isNil(localData) && (
                <mui.core.Box sx={{ flexShrink: 0 }}>
                  <BaseLegend
                    seriesColorByName={localSeriesColorByName}
                    seriesKeys={seriesKeys}
                    chartRef={chartRef}
                    hiddenSeries={hiddenSeries}
                    setHiddenSeries={setHiddenSeries}
                  />
                </mui.core.Box>
              )}
            </>
          )}
        </mui.core.Box>
      </mui.core.Box>
    </mui.core.Box>
  );
};

export default NestedBarChart;
